<template>
    <challenge-layout>
        <template #header>
            <challenge-header :level="currentLevel" :score="levelScore" :threshold="currentLevel?.completionThreshold" show-score/>
        </template>
        <template #form>
            <challenge-form :challenge="challenge" :reset-key="formResetKey" :bot-reset-key="botResetKey" @submit="onSubmit"/>
        </template>
    </challenge-layout>
    <level-entry-dialog v-if="currentLevel" v-model="showLevelEntryDialog" :level="currentLevel" @confirm="onLevelEntryConfirm"/>
    <submission-dialog v-if="currentLevel" v-model="showSubmissionDialog" :loading="submissionLoading" :level="currentLevel" :result="result" :exhausted="exhausted" :level-completed="levelCompleted" :at-max-attempts="atMaxAttempts" @next="onNext" @retry="onRetry"/>
</template>

<script setup lang="ts">
import ChallengeLayout from '../layouts/ChallengeLayout.vue';
import ChallengeForm from '../challenge/ChallengeForm.vue';
import { computed, onMounted, ref } from 'vue';
import { IChallenge, ISubmissionResult } from '@cyber-range/cyber-range-api-ctf-challenge-client';
import { useChallengeStore } from '../../stores/challengeStore';
import ChallengeHeader from '../ChallengeHeader.vue';
import LevelEntryDialog from '../dialogs/LevelEntryDialog.vue';
import { useLevelStore } from '../../stores/levelStore';
import { useScoreStore } from '../../stores/scoreStore';
import ChallengeCategoryPrefix from '../../interfaces/challengeCategoryPrefix';
import SubmissionDialog from '../dialogs/SubmissionDialog.vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePageTitle } from '../../composables/usePageTitle';

const { t } = useI18n();
const route = useRoute();
const challengeStore = useChallengeStore();
const levelStore = useLevelStore();
const scoreStore = useScoreStore();

const challenge = ref<IChallenge>();
const nextChallenge = ref<Promise<IChallenge|undefined>>();
const result = ref<ISubmissionResult>();
const formResetKey = ref(0);
const botResetKey = ref(0);
const exhausted = ref(false);

const showLevelEntryDialog = ref(false);
const showSubmissionDialog = ref(false);
const submissionLoading = ref(false);
const levelCompleted = ref(false);

const levelIndex = Number(route.params.mission) - 1;
const currentLevel = ref(levelStore.levels[levelIndex]);

usePageTitle(computed(() =>
{
    const level = currentLevel.value;
    const text = level ? `${level.namePrefix}` : t('PAGE_TITLE_LEVEL_SELECT');
    return [{ text }];
}));

const levelScore = computed(() =>
{
    if (!currentLevel.value) return 0;
    return scoreStore.getScoreForCategory(`${ChallengeCategoryPrefix.LevelPick}${currentLevel.value.categorySuffix}`);
});

// The current challenge is out of attempts once the team's recorded attempts reach its configured limit.
const atMaxAttempts = computed(() =>
{
    const maxNumberOfAttempts = challenge.value?.settings?.maxNumberOfAttempts;
    if (!challenge.value || !maxNumberOfAttempts) return false;

    return scoreStore.getMyChallengeAttempts(challenge.value.id) >= maxNumberOfAttempts;
});

async function fetchChallenge()
{
    if (currentLevel.value)
    {
        const fetched = await challengeStore.getRandomChallenge(`${ChallengeCategoryPrefix.LevelPick}${currentLevel.value.categorySuffix}`);
        if (fetched)
        {
            challenge.value = fetched;
        }
        return fetched;
    }
}

onMounted(async () =>
{
    const challenge = await fetchChallenge();

    if (!challenge)
    {
        // Upon re-entering a level that's already complete, re-show the submission dialog with challenges exhausted message.
        exhausted.value = true;
        showSubmissionDialog.value = true;
    }
    else
    {
        showLevelEntryDialog.value = true;
    }
});

async function onLevelEntryConfirm()
{
    showLevelEntryDialog.value = false;
}

async function onSubmit(challengeId: string, answer: string | string[])
{
    submissionLoading.value = true;
    showSubmissionDialog.value = true;
    levelCompleted.value = false;

    const scoreBefore = levelScore.value;
    const submissionResult = await challengeStore.submitResponse(challengeId, answer);
    result.value = submissionResult;

    // Record this attempt so the map reflects it; drives whether retry is still offered for this challenge.
    scoreStore.incrementChallengeAttempt(challengeId);

    // Start pre-fetch of next challenge. 
    nextChallenge.value = challengeStore.getRandomChallenge(`${ChallengeCategoryPrefix.LevelPick}${currentLevel.value!.categorySuffix}`);

    if (submissionResult.correct)
    {
        await scoreStore.fetchMyScore();

        const threshold = currentLevel.value!.completionThreshold;
        if (scoreBefore < threshold && levelScore.value >= threshold)
        {
            levelCompleted.value = true;
        }

        // Check if there are more challenges available for this category. If submission is incorrect, we'll still have the current challenge available.
        if (!(await nextChallenge.value))
        {
            exhausted.value = true;
        }
    }    

    submissionLoading.value = false;
    formResetKey.value++;

    if (submissionResult.correct)
    {
        botResetKey.value++;
    }
}

async function onNext()
{
    challenge.value = undefined;
    showSubmissionDialog.value = false;
    // Next only shows after a submission. If we're getting here, we have already pre-fetched the nextChallenge
    challenge.value = await nextChallenge.value;
    window.scrollTo();
}

function onRetry()
{
    showSubmissionDialog.value = false;
}
</script>
